import { isValidElement, useContext } from "react";
import { PortableText } from "@portabletext/react";
import Tabs, { TabContent, TabNav, TabNavItem } from "../../../Tabs/Tabs.jsx";
import CustomComponentsCtx from "./CustomComponentsCtx.jsx";
import { styles } from "./branchBlock.css.ts";

/**
 * The `Branch` functional component renders a tabbed interface using the provided data.
 * It displays a set of tabs with titles and corresponding content within each tab.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.value - An object containing the data necessary for rendering the tabs.
 * @param {Object[]} props.value.branches - An array of branch objects, each representing a tab.
 * @param {string} props.value.branches[].title - The title of the individual branch tab.
 * @param {Object} props.value.branches[].content - The content to display within the associated branch tab.
 * @param {boolean} props.value.expandFirstTab - A flag indicating whether the first tab should be expanded by default.
 *
 * @returns {JSX.Element} The tabbed interface, displaying titles in the navigation area and corresponding content in the tab panels.
 */
const Branch = ({ value }) => {
  const context = useContext(CustomComponentsCtx); // Access context
  const customComponents = context?.customComponents; // Fallback to undefined if context is unavailable
  const { branches, expandFirstTab } = value;

  return (
    <Tabs defaultTabId={expandFirstTab ? "0" : null} w100 theme={"colorful"}>
      <TabNav>
        {branches.map(({ title }, idx) => (
          <TabNavItem id={idx.toString()} title={title} key={idx} />
        ))}
      </TabNav>
      {branches.map(({ content }, idx) => (
        <TabContent id={idx.toString()} key={idx}>
          <div className={styles.contentContainer}>
            {context && CustomComponentsCtx ? (
              <PortableText value={content} components={customComponents} />
            ) : (
              <span className={styles.simpleContent}>
                {isValidElement(content)
                  ? content
                  : "Could not display content"}
              </span>
            )}
          </div>
        </TabContent>
      ))}
    </Tabs>
  );
};

export default Branch;
